import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Send, CheckCircle, AlertCircle, Upload } from 'lucide-react';

// POI definitions
const bankExteriorPOIs = [
  { key: 'lights_fixtures', en: 'Lights & Fixtures', es: 'Luces y Accesorios' },
  { key: 'security', en: 'Security', es: 'Seguridad' },
  { key: 'painting', en: 'Painting', es: 'Pintura' },
  { key: 'cleanliness', en: 'Cleanliness / Vandalism', es: 'Limpieza / Vandalismo' },
  { key: 'signage', en: 'Bank Signage', es: 'Señalización del Banco' },
  { key: 'car_stops', en: 'Car Stops', es: 'Topes para Autos' },
  { key: 'obstructions', en: 'Obstructions', es: 'Obstrucciones' },
  { key: 'landscape', en: 'Landscape', es: 'Paisajismo' },
  { key: 'exits', en: 'All Exits', es: 'Todas las Salidas' },
  { key: 'door_locks', en: 'Door Locks', es: 'Cerraduras' },
  { key: 'gutters', en: 'Gutters & Building Drains', es: 'Canalones y Drenajes' },
  { key: 'asphalt', en: 'Asphalt & Floor Drains', es: 'Asfalto y Drenajes de Piso' },
  { key: 'atm', en: 'ATM', es: 'Cajero Automático' },
  { key: 'windows', en: 'Windows', es: 'Ventanas' },
  { key: 'timers', en: 'Exterior Timer(s)', es: 'Temporizador(es) Exterior' },
  { key: 'compactor', en: 'Compactor(s)', es: 'Compactador(es)' },
  { key: 'night_dep', en: 'Night Depository', es: 'Depósito Nocturno' }
];

const interiorPOIs = [
  { key: 'lights_fixtures', en: 'Lights & Fixtures', es: 'Luces y Accesorios' },
  { key: 'egress_lighting', en: 'Exit/Egress Lighting', es: 'Iluminación de Salida' },
  { key: 'ceiling_tiles', en: 'Ceiling Tiles', es: 'Losetas del Techo' },
  { key: 'flooring', en: 'Flooring & Carpeting', es: 'Pisos y Alfombras' },
  { key: 'doors', en: 'Doors/Locks/Fasteners', es: 'Puertas/Cerraduras/Cierres' },
  { key: 'ac_closet', en: 'A/C Closet / Mechanical Room', es: 'Cuarto de A/C / Cuarto Mecánico' },
  { key: 'phone_room', en: 'Phone Room', es: 'Cuarto de Teléfonos' },
  { key: 'plumbing', en: 'Plumbing (General)', es: 'Plomería (General)' },
  { key: 'kitchen', en: 'Kitchen Appliances', es: 'Electrodomésticos de Cocina' },
  { key: 'exhaust', en: 'Exhaust Fans', es: 'Ventiladores de Extracción' },
  { key: 'panic_bars', en: 'Panic Bars', es: 'Barras Anti-pánico' },
  { key: 'painting', en: 'Painting', es: 'Pintura' },
  { key: 'cleanliness', en: 'Cleanliness', es: 'Limpieza' },
  { key: 'timers', en: 'Timers', es: 'Temporizadores' },
  { key: 'ac_filters', en: 'A/C Filters', es: 'Filtros de A/C' },
  { key: 'windows', en: 'Windows', es: 'Ventanas' },
  { key: 'fire_ext', en: 'Fire Extinguishers (Date)', es: 'Extintores (Fecha)' },
  { key: 'elevator', en: 'Elevator(s)', es: 'Ascensor(es)' },
  { key: 'spaces', en: 'Offices/Suites/Classrooms', es: 'Oficinas/Suites/Aulas' }
];

const PropertyEvaluationForm = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  
  const [formData, setFormData] = useState({
    customer_id: '',
    location_id: '',
    property_type: '',
    date_of_evaluation: new Date().toISOString().split('T')[0],
    evaluator_name: '',
    general_notes: '',
    forward_to_operations: false
  });

  const [inspectionData, setInspectionData] = useState({
    exterior: {},
    interior: {}
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (formData.customer_id) {
      fetchLocations(formData.customer_id);
    }
  }, [formData.customer_id]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data.filter(c => c.status === 'active'));
    } catch (error) {
      toast.error('Failed to fetch customers');
    }
  };

  const fetchLocations = async (customerId) => {
    try {
      const response = await axios.get(`${API}/service-locations/by-customer/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data);
    } catch (error) {
      console.error('Failed to fetch locations');
      setLocations([]);
    }
  };

  const handleInspectionChange = (category, itemKey, field, value) => {
    setInspectionData({
      ...inspectionData,
      [category]: {
        ...inspectionData[category],
        [itemKey]: {
          ...inspectionData[category][itemKey],
          [field]: value
        }
      }
    });
  };

  const getInspectionStatus = (category, itemKey) => {
    return inspectionData[category]?.[itemKey]?.status || 'satisfactory';
  };

  const getInspectionDescription = (category, itemKey) => {
    return inspectionData[category]?.[itemKey]?.description || '';
  };

  const buildInspectionItems = (category, items) => {
    return items.map(item => ({
      category: category,
      item: `${item.en} / ${item.es}`,
      status: inspectionData[category]?.[item.key]?.status || 'satisfactory',
      description: inspectionData[category]?.[item.key]?.description || null,
      photo: inspectionData[category]?.[item.key]?.photo || null
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that items marked "needs_attention" have descriptions
    let hasIssues = false;
    Object.entries(inspectionData).forEach(([category, items]) => {
      Object.entries(items).forEach(([key, item]) => {
        if (item.status === 'needs_attention' && !item.description) {
          hasIssues = true;
        }
      });
    });

    if (hasIssues) {
      toast.error('Please provide descriptions for all items marked "Needs Attention"');
      return;
    }

    setLoading(true);

    try {
      const selectedLocation = locations.find(l => l.id === formData.location_id);
      
      const evaluationPayload = {
        customer_id: formData.customer_id,
        service_location_id: formData.location_id,
        location: selectedLocation?.location_name || '',
        property_type: formData.property_type,
        date_of_inspection: formData.date_of_evaluation,
        inspector_name: formData.evaluator_name,
        exterior_items: buildInspectionItems('exterior', bankExteriorPOIs),
        interior_items: buildInspectionItems('interior', interiorPOIs),
        general_notes: formData.general_notes || null,
        additional_photos: [],
        forward_to_operations: formData.forward_to_operations
      };

      await axios.post(`${API}/property-evaluations`, evaluationPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Property evaluation submitted successfully!');
      navigate('/field/property-evaluations');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit evaluation');
    } finally {
      setLoading(false);
    }
  };

  const renderInspectionCategory = (title, titleEs, category, items) => (
    <div className="space-y-4">
      <h3 className="font-semibold text-lg border-b pb-2">{title} / {titleEs}</h3>
      {items.map((item) => (
        <div key={item.key} className="p-4 bg-slate-50 rounded-lg border">
          <div className="flex items-start justify-between mb-3">
            <Label className="font-medium">{item.en} / {item.es}</Label>
            <RadioGroup
              value={getInspectionStatus(category, item.key)}
              onValueChange={(value) => handleInspectionChange(category, item.key, 'status', value)}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="satisfactory" id={`${category}-${item.key}-sat`} />
                <Label htmlFor={`${category}-${item.key}-sat`} className="font-normal flex items-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Satisfactory / Satisfactorio</span>
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="needs_attention" id={`${category}-${item.key}-att`} />
                <Label htmlFor={`${category}-${item.key}-att`} className="font-normal flex items-center gap-1">
                  <AlertCircle className="h-4 w-4 text-orange-600" />
                  <span className="text-sm">Needs Attention / Necesita Atención</span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {getInspectionStatus(category, item.key) === 'needs_attention' && (
            <div className="mt-3 space-y-3 pl-4 border-l-2 border-orange-400">
              <div>
                <Label className="text-sm text-orange-700">Description Required / Descripción Requerida *</Label>
                <Textarea
                  value={getInspectionDescription(category, item.key)}
                  onChange={(e) => handleInspectionChange(category, item.key, 'description', e.target.value)}
                  placeholder="Describe the deficiency / Describa la deficiencia"
                  rows={2}
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label className="text-sm text-slate-600">
                  <Upload className="h-4 w-4 inline mr-1" />
                  Photo Upload (Required for deficiencies)
                </Label>
                <p className="text-xs text-slate-500">Photo upload feature placeholder</p>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div>
      <Button
        variant="outline"
        onClick={() => navigate('/field')}
        className="mb-4 bg-white"
        data-testid="back-btn"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back / Volver
      </Button>

      <Card className="shadow-xl">
        <div className="h-4" style={{ backgroundColor: '#BDB76B' }} />
        <CardHeader>
          <CardTitle className="text-2xl">
            PROPERTY EVALUATION / EVALUACIÓN DE PROPIEDAD
          </CardTitle>
          <CardDescription>
            Quality assessment and condition documentation / Evaluación de calidad y documentación de condiciones
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Location Selection */}
            <div className="space-y-4 p-4 bg-olive-50 rounded-lg" style={{ backgroundColor: '#F5F5DC' }}>
              <h3 className="font-semibold">Location Information / Información de Ubicación</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="customer">Customer / Cliente *</Label>
                  <select
                    id="customer"
                    data-testid="pe-customer-select"
                    className="w-full border rounded-md px-3 py-2"
                    value={formData.customer_id}
                    onChange={(e) => setFormData({ ...formData, customer_id: e.target.value, location_id: '' })}
                    required
                  >
                    <option value="">Select Customer / Seleccionar Cliente</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>{customer.name}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="property_type">Property Type / Tipo de Propiedad *</Label>
                  <select
                    id="property_type"
                    data-testid="pe-property-type-select"
                    className="w-full border rounded-md px-3 py-2"
                    value={formData.property_type}
                    onChange={(e) => setFormData({ ...formData, property_type: e.target.value })}
                    required
                  >
                    <option value="">Select Type / Seleccionar Tipo</option>
                    <option value="Bank Branch">Bank Branch / Sucursal Bancaria</option>
                    <option value="Commercial Building">Commercial Building / Edificio Comercial</option>
                    <option value="School">School / Escuela</option>
                    <option value="Retail">Retail / Minorista</option>
                    <option value="Mixed Use">Mixed Use / Uso Mixto</option>
                  </select>
                </div>

                {formData.customer_id && (
                  <div className="space-y-2">
                    <Label htmlFor="location">Location / Ubicación *</Label>
                    <select
                      id="location"
                      data-testid="pe-location-select"
                      className="w-full border rounded-md px-3 py-2"
                      value={formData.location_id}
                      onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                      required
                    >
                      <option value="">Select Location / Seleccionar Ubicación</option>
                      {locations.map(location => (
                        <option key={location.id} value={location.id}>
                          {location.name} ({location.type})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="date">Date of Evaluation / Fecha *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date_of_evaluation}
                    onChange={(e) => setFormData({ ...formData, date_of_evaluation: e.target.value })}
                    required
                    data-testid="pe-date-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="evaluator">Evaluator Name / Nombre del Evaluador *</Label>
                  <Input
                    id="evaluator"
                    value={formData.evaluator_name}
                    onChange={(e) => setFormData({ ...formData, evaluator_name: e.target.value })}
                    placeholder="Your name"
                    required
                    data-testid="pe-evaluator-input"
                  />
                </div>
              </div>
            </div>

            {/* Inspection Categories */}
            {formData.location_id && (
              <>
                {renderInspectionCategory('EXTERIOR INSPECTION', 'INSPECCIÓN EXTERIOR', 'exterior', bankExteriorPOIs)}
                {renderInspectionCategory('INTERIOR INSPECTION', 'INSPECCIÓN INTERIOR', 'interior', interiorPOIs)}

                {/* General Notes */}
                <div className="space-y-2">
                  <Label htmlFor="notes">General Notes / Notas Generales</Label>
                  <Textarea
                    id="notes"
                    value={formData.general_notes}
                    onChange={(e) => setFormData({ ...formData, general_notes: e.target.value })}
                    rows={4}
                    placeholder="Any additional comments / Comentarios adicionales"
                    data-testid="pe-notes-input"
                  />
                </div>

                {/* Forward to Operations */}
                <div className="flex items-center space-x-2 p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <Checkbox
                    id="forward"
                    checked={formData.forward_to_operations}
                    onCheckedChange={(checked) => setFormData({ ...formData, forward_to_operations: checked })}
                    data-testid="pe-forward-checkbox"
                  />
                  <Label htmlFor="forward" className="font-medium">
                    Forward to Operations / Enviar a Operaciones
                    <span className="block text-xs text-slate-600 font-normal">
                      Check this if deficiencies require management follow-up / Marque si las deficiencias requieren seguimiento
                    </span>
                  </Label>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  data-testid="pe-submit-btn"
                  style={{ backgroundColor: '#6B9F3E' }}
                >
                  <Send className="h-4 w-4 mr-2" />
                  {loading ? 'Submitting... / Enviando...' : 'Submit Evaluation / Enviar Evaluación'}
                </Button>
              </>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default PropertyEvaluationForm;
